// GasStationCards component - Display fuel statistics by gas station

import React from "react";
import { formatCurrency, formatNumber } from "../../utils/formatters";

// Import gas station logos
import bangchakLogo from "../../data/logo/บางจาก.png";
import ptttLogo from "../../data/logo/ปตท.png";

const GasStationCards = ({ data }) => {
  // Map gas station names to their logos
  const getGasStationLogo = (stationName) => {
    const logoMap = {
      บางจาก: bangchakLogo,
      ปตท: ptttLogo,
      // Add more mappings as needed
    };
    return logoMap[stationName] || null;
  };

  // Get color scheme for each gas station
  const getStationColor = (stationName) => {
    const colorMap = {
      บางจาก: {
        bg: "bg-blue-50",
        border: "border-blue-200",
        text: "text-blue-700",
        accent: "text-blue-600",
      },
      ปตท: {
        bg: "bg-orange-50",
        border: "border-orange-200",
        text: "text-orange-700",
        accent: "text-orange-600",
      },
      เชลล์: {
        bg: "bg-yellow-50",
        border: "border-yellow-200",
        text: "text-yellow-700",
        accent: "text-yellow-600",
      },
      ไม่ระบุ: {
        bg: "bg-gray-50",
        border: "border-gray-200",
        text: "text-gray-700",
        accent: "text-gray-600",
      },
    };
    return (
      colorMap[stationName] || {
        bg: "bg-gray-50",
        border: "border-gray-200",
        text: "text-gray-700",
        accent: "text-gray-600",
      }
    );
  };

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div>
      <h4 className="text-xl font-semibold text-orange-800 mb-4 flex items-center">
        <svg
          className="w-6 h-6 mr-2 text-orange-600"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
        </svg>
        สถิติการเติมน้ำมันตามปั๊ม
      </h4>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {data.map((station, index) => {
          const colors = getStationColor(station.gas_station);
          const logo = getGasStationLogo(station.gas_station);

          return (
            <div
              key={index}
              className={`${colors.bg} ${colors.border} border-2 rounded-lg p-4 hover:shadow-md transition-shadow duration-200`}
            >
              {/* Header with logo and name */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  {logo ? (
                    <img
                      src={logo}
                      alt={station.gas_station}
                      className="w-10 h-10 object-contain"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                      <svg
                        className={`w-6 h-6 ${colors.accent}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                  <h4 className={`font-semibold ${colors.text}`}>
                    {station.gas_station}
                  </h4>
                </div>
                <div className={`${colors.accent} font-bold text-sm`}>
                  {station.fill_count} ครั้ง
                </div>
              </div>

              {/* Statistics */}
              <div className="space-y-2">
                {/* Total Amount */}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">จำนวนเงินรวม:</span>
                  <span className={`text-sm font-semibold ${colors.text}`}>
                    {formatCurrency(station.total_amount)}
                  </span>
                </div>

                {/* Total Volume */}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">ปริมาณรวม:</span>
                  <span className={`text-sm font-semibold ${colors.text}`}>
                    {formatNumber(station.total_volume)} ลิตร
                  </span>
                </div>

                {/* Average Price */}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">
                    ราคาเฉลี่ย/ลิตร:
                  </span>
                  <span className={`text-sm font-semibold ${colors.text}`}>
                    {formatNumber(station.avg_price)} บาท
                  </span>
                </div>

                {/* Average per Fill */}
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-xs text-gray-600">เฉลี่ย/ครั้ง:</span>
                  <span className={`text-sm font-bold ${colors.accent}`}>
                    {formatCurrency(station.avg_amount_per_fill)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GasStationCards;
